import {
  useRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useReducer,
} from "react";
import { Pagination, Table, Whisper } from "rsuite";
import { useHistory } from "react-router-dom";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import { useAppSelector } from "../../core/redux";
import { useAppDispatch } from "../../core/redux";
import editIcon from "../../assets/img/edit-icon.svg";
import { ActionsTaskTable } from "../../core/redux/slices/sliceTaskTable";
import { MenuPopover, TextCell } from "../../components/tableItem.component";
import { useAlertDialog } from "../../components/alertDialog";
import {
  ApiSchemaPatientTask,
  ApiSchemaPatientTaskPaginated,
} from "../../models/api/patientTask";
import { ApiPatientTask } from "../../core/apis";
import { StorageAuth } from "../../core/localStorage/StorageAuth";
import { AppUserRoles } from "../../config/constants";
import { sortTableData } from "../../utils/tableFilter";
import { BooleanToggleFilterIcon } from "../../components/booleanToggleFilterIcon.component";
import { IsDefaultForDHHSToggle } from "../../components/isDefaultForDHHSToggle.component";
import {
  useTablePaginationSearchFilter,
  IPaginationSearchFilter,
} from "../../hooks";
import { TableHeader } from "../../components";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaPatientTask & {
  sl: number;
  checked?: boolean;
  };

export const TaskList = () => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selected, setSelected] = useState<TableData[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const triggerRef = useRef<any>();
  const routerHistory = useHistory();
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();
  const {
    pageNumber,
    pageSize,
    isLoading,
    searchKey,
    nextPageToken,
    shouldRefetch,
  } = useAppSelector((state) => state.taskTable);
  const dispatch = useAppDispatch();
  const apiParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      SearchKey: searchKey,
      PageNumber: pageNumber,
      NextPageToken: nextPageToken,
      PageSize: pageSize,
    };
  }, [searchKey, pageNumber, nextPageToken, pageSize]);
  const { apiData, refetch } = useTablePaginationSearchFilter<
    ApiSchemaPatientTaskPaginated,
    typeof ActionsTaskTable
  >(ActionsTaskTable, {
    apiRoute: AppRouteApi.PatientTask.Paginated(),
    customParams: apiParams,
  });

  const [sortBy, setSortBy] = useState<keyof TableData>("sl");
  const [sortAsc, setSortAsc] = useState(true);
  const [isDefaultForDHHSFilter, toggleIsDefaultForDHHSFilter] = useReducer(
    (curr: number) => (curr + 1) % 3,
    2
  );
  const isSuperAdmin = StorageAuth.Role === AppUserRoles.SuperAdmin;

  useEffect(
    () =>
      setTableData(
        apiData?.list?.map((Task, index) => ({
          sl: (pageNumber - 1) * pageSize + index + 1,
          ...Task,
          name: Task.displayName ?? Task.name,
          precedence: Task.precedence ?? 0,
        })) ?? []
      ),
    [apiData, pageNumber, pageSize]
  );

  const refetchTableData = useCallback(() => {
    dispatch(ActionsTaskTable.setRefetch(true));
  }, [dispatch]);

  const filteredTableData = useMemo(() => {
    return sortTableData(tableData, sortBy, sortAsc);
  }, [sortAsc, sortBy, tableData]);

  const CellRowSelection = useMemo((): ReactNode => {
    return (rowData: TableData) => (
      <input
        type="checkbox"
        checked={!!rowData.checked}
        onChange={(ev) =>
          rowData.sKey &&
          setTableData((tableData) =>
            tableData?.map((td) =>
              td.sKey === rowData.sKey
                ? {
                    ...td,
                    checked: ev.target.checked,
                  }
                : td
            )
          )
        }
      />
    );
  }, []);

  const selectAllRow = useCallback((checked: boolean) => {
    setTableData((tableData) =>
      tableData.map((td) => ({
        ...td,
        checked: checked,
      }))
    );
  }, []);

  const isAllChecked = useMemo(
    () => tableData.length > 0 && tableData.every((td) => !!td.checked),
    [tableData]
  );

  useEffect(() => {
    setSelected(tableData.filter((td) => !!td.checked));
  }, [tableData]);

  const onDelete = useCallback(
    () =>
      alertDialogCtrl.open({
        message: "Are you sure you want to delete the selected Tasks?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          setIsDeleting(true);
          dispatch(ActionsTaskTable.setIsLoading(true));
          await Promise.all(
            selected.map(
              async (item) => item.sKey && (await deleteItem(item.sKey))
            )
          );
          dispatch(ActionsTaskTable.setIsLoading(false));
          dispatch(ActionsTaskTable.setRefetch(true));
          setIsDeleting(false);
        },
      }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  const handleSelectMenu = async (
    eventKey: string,
    event: Event,
    sKey?: string
  ) => {
    if (eventKey === "delete" && sKey)
      alertDialogCtrl.open({
        message: "Are you sure you want to delete this Task?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsTaskTable.setIsLoading(true));
          await deleteItem(sKey);
          dispatch(ActionsTaskTable.setIsLoading(false));
        },
      });
    else if (eventKey === "update" && sKey)
      routerHistory.push(AppRouteUi.ManageTaskList.Update.ByTaskId(sKey));
  };

  const deleteItem = async (sKey: string) => {
    const deleted = await ApiPatientTask.Delete(sKey);
    if (deleted) {
      refetchTableData();
    }
  };
  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  return (
    <div>
      {alertDialogComponent}
      <TableHeader
        buttonText="Add New Task"
        redirectPath={AppRouteUi.ManageTaskList.Add()}
        onDelete={onDelete}
        pageSize={pageSize}
        TableAction={ActionsTaskTable}
        isDeleting={isDeleting}
        isSelected={!!selected.length}
      />
      <Table
        autoHeight
        data={filteredTableData}
        loading={isLoading}
        onScroll={() => triggerRef.current?.close()}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Data Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
        rowClassName="table-row-hover"
        bordered
        cellBordered
        hover
        classPrefix="rs-table"
        className="table-striped"
      >
        <Column align="center" flexGrow={0.3}>
          <HeaderCell className="fw-bold text-black">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={(ev) => selectAllRow(ev.target.checked)}
            />
          </HeaderCell>
          <Cell>{CellRowSelection}</Cell>
        </Column>
        <Column align="center" flexGrow={0.3} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="S/L">S/L</span>
          </HeaderCell>
          <TextCell component="ManageTaskList" dataKey="sl" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Name">Name</span>
          </HeaderCell>
          <TextCell component="ManageTaskList" dataKey="name" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Display Name">Display Name</span>
          </HeaderCell>
          <TextCell component="ManageTaskList" dataKey="displayName" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Procedure Code">Procedure Code</span>
          </HeaderCell>
          <TextCell component="ManageTaskList" dataKey="procedureCode" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Precedence">Precedence</span>
          </HeaderCell>
          <TextCell component="ManageTaskList" dataKey="precedence" />
        </Column>

        <Column align="center" flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Default">Default</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div title={rowData.isDefault ? "Default Task" : "Non-Default Task"}>
                <i className={`fas fa-${rowData.isDefault ? 'check' : 'minus'}-circle ${rowData.isDefault ? 'text-danger' : 'text-muted'}`} />
              </div>
            )}
          </Cell>
        </Column>

        {isSuperAdmin && (
          <Column align="center" flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span
                title="Default for DHS?"
                className="click"
                onClick={() => {
                  setSortBy("isDefaultForDHHS");
                  toggleIsDefaultForDHHSFilter();
                }}
              >
                Default for DHS?
                <BooleanToggleFilterIcon
                  filterValue={
                    isDefaultForDHHSFilter === 0
                      ? "true"
                      : isDefaultForDHHSFilter === 1
                      ? "false"
                      : "default"
                  }
                  className="ms-2 toggle-filter-icon"
                />
              </span>
            </HeaderCell>
            <Cell>
              {(rowData: ApiSchemaPatientTask) => (
                <IsDefaultForDHHSToggle task={rowData} />
              )}
            </Cell>
          </Column>
        )}
        {isSuperAdmin && (
          <Column align="center" fixed="right" flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Action">Action</span>
            </HeaderCell>
            <Cell>
              {(rowData: TableData) =>
                (StorageAuth.Role === AppUserRoles.SuperAdmin ||
                  !rowData.isDefault) && (
                  <Whisper
                    placement="left"
                    trigger="click"
                    ref={triggerRef}
                    speaker={(props, ref) => {
                      const { className, left, top, onClose } = props;
                      return (
                        <MenuPopover
                          ref={ref}
                          className={className}
                          style={{ left, top }}
                          onClose={onClose}
                          onSelect={(eventKey: string, event: Event) =>
                            handleSelectMenu(eventKey, event, rowData.sKey)
                          }
                        />
                      );
                    }}
                  >
                    <img
                      src={editIcon}
                      alt="Update/Delete"
                      className="me-1 click"
                      height={15}
                    />
                  </Whisper>
                )
              }
            </Cell>
          </Column>
        )}
      </Table>

      {apiData?.list && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            size="md"
            total={apiData?.list.length}
            pages={Math.ceil(apiData.totalCount / pageSize)}
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page: any) => dispatch(ActionsTaskTable.goToPage(page))}
          />
        </div>
      )}
    </div>
  );
};
